import { Paper, Typography } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import type { CockpitPhaseStepView } from '../model/cockpitViewModel';
import { useCockpitStyles } from './cockpitStyles';

export interface PhaseStepperSectionProps {
  phases: CockpitPhaseStepView[] | null;
  /** The phase the artifacts list is filtered by; its step reads as pressed. */
  selectedKey?: string | null;
  onSelectPhase?: (key: string) => void;
}

const STATE_LABELS: Record<CockpitPhaseStepView['state'], string> = {
  completed: 'concluída',
  active: 'fase atual',
  pending: 'pendente',
};

const StepIcon = ({ state }: { state: CockpitPhaseStepView['state'] }) => {
  const classes = useCockpitStyles();
  if (state === 'completed') {
    return (
      <CheckCircleIcon
        className={`${classes.phaseIcon} ${classes.phaseIconCompleted}`}
      />
    );
  }
  if (state === 'active') {
    return (
      <FiberManualRecordIcon
        className={`${classes.phaseIcon} ${classes.phaseIconActive}`}
      />
    );
  }
  return (
    <RadioButtonUncheckedIcon
      className={`${classes.phaseIcon} ${classes.phaseIconPending}`}
    />
  );
};

const StepContent = ({
  step,
  selected,
  onSelectPhase,
}: {
  step: CockpitPhaseStepView;
  selected: boolean;
  onSelectPhase?: (key: string) => void;
}) => {
  const classes = useCockpitStyles();
  const body = (
    <>
      <StepIcon state={step.state} />
      <span className={classes.phaseStepLabel}>{step.label}</span>
    </>
  );
  // a phase with artifacts is a way into them (RN-COCK-08); one without stays
  // a plain milestone, never a dead button
  if (step.artifactIds.length === 0 || !onSelectPhase) return body;
  return (
    <button
      type="button"
      className={classes.phaseStepButton}
      aria-pressed={selected}
      aria-label={`${step.label}: ${STATE_LABELS[step.state]}; ver artefatos`}
      onClick={() => onSelectPhase(step.key)}
    >
      {body}
    </button>
  );
};

/**
 * Where the activity stands in the pipeline (RN-COCK-01), and the filter into
 * each phase's artifacts (RN-COCK-08/09). The section is not drawn at all
 * when the model has no phase evidence — the stepper never guesses a
 * position. A plain ordered list rather than Material's Stepper, so the list
 * semantics and `aria-current` reach assistive technology without fighting
 * the widget.
 */
export const PhaseStepperSection = ({
  phases,
  selectedKey = null,
  onSelectPhase,
}: PhaseStepperSectionProps) => {
  const classes = useCockpitStyles();
  if (!phases) return null;
  return (
    <Paper
      component="section"
      className={classes.panelLead}
      aria-labelledby="phase-stepper-title"
    >
      <Typography
        id="phase-stepper-title"
        component="h2"
        className={classes.visuallyHidden}
      >
        Fases da atividade
      </Typography>
      <div className={classes.phaseStepperScroll}>
        <ol className={classes.phaseStepper} aria-label="Fases da atividade">
          {phases.map(step => {
            const interactive = step.artifactIds.length > 0 && onSelectPhase;
            return (
              <li
                key={step.key}
                className={`${classes.phaseStep} ${
                  step.state === 'active' ? classes.phaseStepActive : ''
                }`}
                aria-current={step.state === 'active' ? 'step' : undefined}
                aria-label={
                  interactive
                    ? undefined
                    : `${step.label}: ${STATE_LABELS[step.state]}`
                }
              >
                <StepContent
                  step={step}
                  selected={selectedKey === step.key}
                  onSelectPhase={onSelectPhase}
                />
              </li>
            );
          })}
        </ol>
      </div>
    </Paper>
  );
};
